"use client";

import React, { Suspense, lazy, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Lazy load example components
import ControlComboboxExample from '@/examples/ControlComboboxExample';
import MCMComboboxExample from '@/examples/MCMComboboxExample';
import MCMComboboxAssetsOnlyExample from '@/examples/MCMComboboxAssetsOnlyExample';
import MCMComboboxSuppliersOnlyExample from '@/examples/MCMComboboxSuppliersOnlyExample';
import MCMComboboxSupportingDocsOnlyExample from '@/examples/MCMComboboxSupportingDocsOnlyExample';
import AnzsicComboboxExample from '@/examples/AnzsicComboboxExample';
import FrameworkComboboxExample from '@/examples/FrameworkComboboxExample';
import RisksComboboxExample from '@/examples/RisksComboboxExample';
import AssetsComboboxExample from '@/examples/AssetsComboboxExample';
import DataTypeComboboxExample from '@/examples/DataTypeComboboxExample';
import ObjectivesComboboxExample from '@/examples/ObjectivesComboboxExample';
import LegalObligationsComboboxExample from '@/examples/LegalObligationsComboboxExample';
import TeamsComboboxExample from '@/examples/TeamsComboboxExample';
import { ISMSRoleComboboxExample } from '@/examples/ISMSRoleComboboxExample';
import { ISMSCommitteeComboboxExample } from '@/examples/ISMSCommitteeComboboxExample';
import { AuditorExportExample } from '@/examples/auditor-export-example';
// Modal examples
import ModalTextOnlyExample from '@/examples/modals/ModalTextOnlyExample';
import ModalTabbedExample from '@/examples/modals/ModalTabbedExample';
import ModalUserSelectionExample from '@/examples/modals/ModalUserSelectionExample';
import ModalTeamSelectionExample from '@/examples/modals/ModalTeamSelectionExample';
import ModalConfirmationExample from '@/examples/modals/ModalConfirmationExample';
import ModalFormExample from '@/examples/modals/ModalFormExample';
import ModalStepperExample from '@/examples/modals/ModalStepperExample';
import ModalListPickerExample from '@/examples/modals/ModalListPickerExample';
import ModalTwoColumnInfoExample from '@/examples/modals/ModalTwoColumnInfoExample';
import ModalComponentPickerExample from '@/examples/modals/ModalComponentPickerExample';
import ModalFeatureSelectionExample from '@/examples/modals/ModalFeatureSelectionExample';
import ModalComponentSelectionMCMExample from '@/examples/modals/ModalComponentSelectionMCMExample';
import ModalDataPoolsSuggestExample from '@/examples/modals/ModalDataPoolsSuggestExample';
import ModalDataTypesSuggestExample from '@/examples/modals/ModalDataTypesSuggestExample';
import ModalCompanyContextExample from '@/examples/modals/ModalCompanyContextExample';
import ModalSupplierSelectionExample from '@/examples/modals/ModalSupplierSelectionExample';
import ModalProcessesSuggestExample from '@/examples/modals/ModalProcessesSuggestExample';
import ModalPrivacyPolicyImportExample from '@/examples/modals/ModalPrivacyPolicyImportExample';
import ModalCollectionNoticeImportExample from '@/examples/modals/ModalCollectionNoticeImportExample';
import ModalControlQuestionsExample from '@/examples/modals/ModalControlQuestionsExample';
import ModalAssuranceTaskSchedulingExample from '@/examples/modals/ModalAssuranceTaskSchedulingExample';

const exampleComponents = {
  'control-combobox': ControlComboboxExample,
  'anzsic-combobox': AnzsicComboboxExample,
  'framework-combobox': FrameworkComboboxExample,
  'risks-combobox': RisksComboboxExample,
  'assets-combobox': AssetsComboboxExample,
  'objectives-combobox': ObjectivesComboboxExample,
  'legal-obligations-combobox': LegalObligationsComboboxExample,
  'teams-combobox': TeamsComboboxExample,
  'isms-role-combobox': ISMSRoleComboboxExample,
  'isms-committee-combobox': ISMSCommitteeComboboxExample,
  'auditor-export': AuditorExportExample,
  'data-type-combobox': DataTypeComboboxExample,
  'mcm-combobox': MCMComboboxExample,
  'mcm-combobox-assets-only': MCMComboboxAssetsOnlyExample,
  'mcm-combobox-suppliers-only': MCMComboboxSuppliersOnlyExample,
  'mcm-combobox-supporting-docs-only': MCMComboboxSupportingDocsOnlyExample,
  // Modal examples
  'modal-text-only': ModalTextOnlyExample,
  'modal-tabbed': ModalTabbedExample,
  'modal-user-selection': ModalUserSelectionExample,
  'modal-team-selection': ModalTeamSelectionExample,
  'modal-confirmation': ModalConfirmationExample,
  'modal-form': ModalFormExample,
  'modal-stepper': ModalStepperExample,
  'modal-list-picker': ModalListPickerExample,
  'modal-two-column-info': ModalTwoColumnInfoExample,
  'modal-component-picker': ModalComponentPickerExample,
  'modal-feature-selection': ModalFeatureSelectionExample,
  'modal-component-selection': ModalComponentSelectionMCMExample,
  'modal-data-pools-suggest': ModalDataPoolsSuggestExample,
  'modal-data-types-suggest': ModalDataTypesSuggestExample,
  'modal-company-context': ModalCompanyContextExample,
  'modal-supplier-selection': ModalSupplierSelectionExample,
  'modal-processes-suggest': ModalProcessesSuggestExample,
  'modal-privacy-policy-import': ModalPrivacyPolicyImportExample,
  'modal-collection-notice-import': ModalCollectionNoticeImportExample,
  'modal-control-questions': ModalControlQuestionsExample,
  'modal-assurance-task-scheduling': ModalAssuranceTaskSchedulingExample,
};

interface Example {
  id: string;
  title: string;
  description: string;
  path: string;
  tags?: string[];
}

const examples: Example[] = [
  // Modal examples
  {
    id: 'modal-text-only',
    title: 'Modal: Text Only',
    description: 'A simple informational modal using the onboarding aesthetic.',
    path: '/examples/modals/text-only',
    tags: ['UI', 'Modal']
  },
  {
    id: 'modal-tabbed',
    title: 'Modal: Tabbed',
    description: 'A tabbed modal with three sections and sticky footer actions.',
    path: '/examples/modals/tabbed',
    tags: ['UI', 'Modal', 'Tabs']
  },
  {
    id: 'modal-feature-selection',
    title: 'Modal: Feature Selection',
    description: 'Enable/disable platform features with search and category filters.',
    path: '/examples/modals/feature-selection',
    tags: ['UI', 'Modal', 'Selection']
  },
  {
    id: 'modal-data-pools-suggest',
    title: 'Modal: Data Pools Suggest',
    description: 'AI suggested data pools based on your Data Labels, Data Pools, Suppliers, and Context.',
    path: '/examples/modals/data-pools-suggest',
    tags: ['UI', 'Modal', 'Data Pools']
  },
  {
    id: 'modal-data-types-suggest',
    title: 'Modal: Data Types Suggest',
    description: 'AI suggested data types based on your Data Labels, Data Pools, Suppliers, and Context.',
    path: '/examples/modals/data-types-suggest',
    tags: ['UI', 'Modal', 'Data Types']
  },
  {
    id: 'modal-company-context',
    title: 'Modal: Company Context',
    description: 'Company context modal with search and category filters.',
    path: '/examples/modals/company-context',
    tags: ['UI', 'Modal', 'Context']
  },
  {
    id: 'modal-supplier-selection',
    title: 'Modal: Supplier Selection',
    description: 'Supplier picker using MultiSupplierCombobox.',
    path: '/examples/modals/supplier-selection',
    tags: ['UI', 'Modal', 'Suppliers']
  },
  {
    id: 'modal-processes-suggest',
    title: 'Modal: Processes Suggest',
    description: 'AI suggested processes based on your Data Labels, Data Pools, Suppliers, and Context.',
    path: '/examples/modals/processes-suggest',
    tags: ['UI', 'Modal', 'Processes']
  },
  {
    id: 'modal-privacy-policy-import',
    title: 'Modal: Privacy Policy Import',
    description: 'Upload your Privacy Policy or download our template and upload it here.',
    path: '/examples/modals/privacy-policy-import',
    tags: ['UI', 'Modal', 'Privacy Policy']
  },
  {
    id: 'modal-collection-notice-import',
    title: 'Modal: Collection Notice Import',
    description: 'Upload your Collection Notice or download our template and upload it here.',
    path: '/examples/modals/collection-notice-import',
    tags: ['UI', 'Modal', 'Collection Notice']
  },
  {
    id: 'modal-control-questions',
    title: 'Modal: Control Questions',
    description: 'Answer questions to automatically populate your Statement of Applicability.',
    path: '/examples/modals/control-questions',
    tags: ['UI', 'Modal', 'SOA', 'Controls']
  },
  {
    id: 'modal-assurance-task-scheduling',
    title: 'Modal: Assurance Task Scheduling',
    description: 'Select and schedule recurring assurance tasks to automate your compliance program.',
    path: '/examples/modals/assurance-task-scheduling',
    tags: ['UI', 'Modal', 'Assurance', 'Scheduling']
  },
  {
    id: 'modal-component-selection',
    title: 'Modal: Component Selection',
    description: 'MCM selector plus domain comboboxes (controls, assets, risks, teams, legal, data types).',
    path: '/examples/modals/component-selection',
    tags: ['UI', 'Modal', 'MCM']
  },
  {
    id: 'modal-component-picker',
    title: 'Modal: Card Picker',
    description: 'Selectable card grid with visual selection state.',
    path: '/examples/modals/card-picker',
    tags: ['UI', 'Modal', 'Cards']
  },
  {
    id: 'modal-user-selection',
    title: 'Modal: User Selection',
    description: 'User picker using MultiUserCombobox.',
    path: '/examples/modals/user-selection',
    tags: ['UI', 'Modal', 'Users']
  },
  {
    id: 'modal-team-selection',
    title: 'Modal: Team Selection',
    description: 'Team picker using TeamsCombobox and TeamsProvider.',
    path: '/examples/modals/team-selection',
    tags: ['UI', 'Modal', 'Teams']
  },
  {
    id: 'modal-confirmation',
    title: 'Modal: Confirmation',
    description: 'Destructive action confirmation with icon and danger styling.',
    path: '/examples/modals/confirmation',
    tags: ['UI', 'Modal', 'Confirm']
  },
  {
    id: 'modal-form',
    title: 'Modal: Form',
    description: 'Form layout with inputs, select and checkbox.',
    path: '/examples/modals/form',
    tags: ['UI', 'Modal', 'Form']
  },
  {
    id: 'modal-stepper',
    title: 'Modal: Stepper',
    description: 'Three-step wizard with Back/Next/Finish.',
    path: '/examples/modals/stepper',
    tags: ['UI', 'Modal', 'Stepper']
  },
  {
    id: 'modal-list-picker',
    title: 'Modal: List Picker',
    description: 'Command palette style list with multi-select.',
    path: '/examples/modals/list-picker',
    tags: ['UI', 'Modal', 'Command']
  },
  {
    id: 'modal-two-column-info',
    title: 'Modal: Two Column',
    description: 'Information-heavy two-column layout with actions.',
    path: '/examples/modals/two-column',
    tags: ['UI', 'Modal', 'Layout']
  },
  {
    id: 'mcm-combobox',
    title: 'MCM Combobox',
    description: 'A multi-category combobox for linking items across modules (controls, assets, risks, policies, etc.).',
    path: '/examples/mcm-combobox',
    tags: ['UI', 'MCM', 'Combobox']
  },
  {
    id: 'mcm-combobox-assets-only',
    title: 'MCM Combobox (Assets Only)',
    description: 'Assets-only selection without choosing a primary control; targets provided externally.',
    path: '/examples/mcm-combobox-assets-only',
    tags: ['UI', 'MCM', 'Combobox', 'Assets']
  },
  {
    id: 'mcm-combobox-suppliers-only',
    title: 'MCM Combobox (Suppliers Only)',
    description: 'Suppliers-only selection without a primary control; external targets provided.',
    path: '/examples/mcm-combobox-suppliers-only',
    tags: ['UI', 'MCM', 'Combobox', 'Suppliers']
  },
  {
    id: 'mcm-combobox-supporting-docs-only',
    title: 'MCM Combobox (Supporting Docs Only)',
    description: 'Supporting documents only; facet filters for type/status/classification available.',
    path: '/examples/mcm-combobox-supporting-docs-only',
    tags: ['UI', 'MCM', 'Combobox', 'Documents']
  },
  {
    id: 'data-type-combobox',
    title: 'Data Type Combobox',
    description: 'A searchable combobox for selecting data retention types, powered by dataRetentionService.',
    path: '/examples/data-type-combobox',
    tags: ['UI', 'Form', 'Search', 'Data Retention']
  },
  {
    id: 'auditor-export',
    title: 'Auditor Export',
    description: 'A dialog component for exporting audit packs with framework selection and date range filtering.',
    path: '/examples/auditor-export',
    tags: ['Export', 'Audit', 'Dialog']
  },
  {
    id: 'control-combobox',
    title: 'Control Combobox',
    description: 'A searchable combobox component for selecting controls with support for single and multi-select.',
    path: '/examples/control-combobox',
    tags: ['UI', 'Form', 'Search']
  },
  {
    id: 'anzsic-combobox',
    title: 'ANZSIC Combobox',
    description: 'A searchable combobox for ANZSIC codes with debounced search and single/multi-select support.',
    path: '/examples/anzsic-combobox',
    tags: ['UI', 'Form', 'Search', 'ANZSIC']
  },
  {
    id: 'framework-combobox',
    title: 'Framework Combobox',
    description: 'A searchable combobox for selecting multiple frameworks with filtering and select/deselect all functionality.',
    path: '/examples/framework-combobox',
    tags: ['UI', 'Form', 'Search', 'Frameworks']
  },
  {
    id: 'risks-combobox',
    title: 'Risks Combobox',
    description: 'A searchable combobox for selecting multiple risks with category filtering and rich item display.',
    path: '/examples/risks-combobox',
    tags: ['UI', 'Form', 'Search', 'Risks']
  },
  {
    id: 'assets-combobox',
    title: 'Assets Combobox',
    description: 'A searchable combobox for selecting multiple assets with type filtering and rich item display.',
    path: '/examples/assets-combobox',
    tags: ['UI', 'Form', 'Search', 'Assets']
  },
  {
    id: 'objectives-combobox',
    title: 'Objectives Combobox',
    description: 'A combobox for selecting objectives with search and multi-select capabilities.',
    path: '/examples/objectives-combobox',
    tags: ['UI', 'Form', 'Search', 'Objectives']
  },
  {
    id: 'legal-obligations-combobox',
    title: 'Legal Obligations Combobox',
    description: 'A searchable combobox for legal obligations with filtering by framework and clause.',
    path: '/examples/legal-obligations-combobox',
    tags: ['UI', 'Form', 'Search', 'Legal']
  },
  {
    id: 'teams-combobox',
    title: 'Teams Combobox',
    description: 'A searchable combobox for selecting teams with support for single and multi-select.',
    path: '/examples/teams-combobox',
    tags: ['UI', 'Form', 'Search', 'Teams']
  },
  {
    id: 'isms-role-combobox',
    title: 'ISMS Role Combobox',
    description: 'A searchable combobox for selecting ISMS roles with support for single and multi-select.',
    path: '/examples/isms-role-combobox',
    tags: ['UI', 'Form', 'Search', 'Roles', 'ISMS']
  },
  {
    id: 'isms-committee-combobox',
    title: 'ISMS Committee Combobox',
    description: 'A searchable combobox for selecting ISMS committees with support for single and multi-select.',
    path: '/examples/isms-committee-combobox',
    tags: ['UI', 'Form', 'Search', 'Committees', 'ISMS']
  },
  // Add more examples here as they are created
];

export default function ExamplesIndex() {
  const [activeExample, setActiveExample] = useState<string | null>(null);
  const activeMeta = useMemo(() => examples.find(e => e.id === activeExample) || null, [activeExample]);
  const isModalActive = !!activeMeta?.tags?.includes('Modal');
  const ExampleComponent = activeExample ? exampleComponents[activeExample as keyof typeof exampleComponents] : null;

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Component Examples</h1>
        <p className="text-muted-foreground">
          Browse and test our collection of reusable UI components and examples.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {examples.map((example) => (
          <Card key={example.id} className="flex flex-col h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">{example.title}</CardTitle>
                {example.tags?.length && (
                  <div className="flex gap-1">
                    {example.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <CardDescription>{example.description}</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto pt-0">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setActiveExample(example.id)}
              >
                View Example
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-12 p-6 border rounded-lg bg-muted/50">
        <h2 className="text-xl font-semibold mb-4">Getting Started</h2>
        <p className="text-muted-foreground mb-4">
          To use these components in your application, import them from the appropriate location in the <code>@/components</code> directory.
        </p>
        <div className="space-y-2">
          <p className="font-medium">Example usage:</p>
          <pre className="bg-muted p-4 rounded-md text-sm overflow-x-auto">
            {`import { AnzsicSearchCombobox } from "@/components/ui/AnzsicSearchCombobox";

function YourComponent() {
  const [selected, setSelected] = React.useState<string[]>([]);
  
  return (
    <AnzsicSearchCombobox
      selected={selected}
      onSelect={(value) => setSelected(prev => [...prev, value])}
      onDeselect={(value) => setSelected(prev => prev.filter(v => v !== value))}
      placeholder="Search ANZSIC codes..."
    />
  );
}`}
          </pre>
        </div>
      </div>

      {/* Preview dialog only for non-modal examples */}
      <Dialog open={!!activeExample && !isModalActive} onOpenChange={(open) => !open && setActiveExample(null)}>
        <DialogContent className="w-[98vw] max-w-[1800px] max-h-[95vh] p-0 bg-secondary overflow-y-auto">
          <DialogHeader className="border-b p-6 bg-card">
            <DialogTitle className="text-2xl font-semibold">
              {activeMeta?.title || 'Example'}
            </DialogTitle>
          </DialogHeader>
          <div className="p-0 overflow-y-auto h-full">
            <Suspense fallback={
              <div className="flex items-center justify-center p-16">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
              </div>
            }>
              <div className="w-full max-w-full min-h-[70vh] p-8">
                {ExampleComponent && <ExampleComponent />}
              </div>
            </Suspense>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline render for modal examples to avoid nested dialogs and extra click */}
      {isModalActive && ExampleComponent && (
        <div className="p-0">
          <ExampleComponent />
        </div>
      )}
    </div>
  );
}